import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Container,
  TextField,
  CircularProgress,
  Backdrop,
  Tab,
  Tabs,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  Gesture as DrawIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  Crop as CropIcon,
  RestartAlt as ResetIcon
} from '@mui/icons-material';
import SignatureCanvas from 'react-signature-canvas';
import { PDFViewer } from '@react-pdf/renderer';
import ActaPDF from './ActaPDF';
import { db } from '../firebase';
import { doc, updateDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { useParams } from 'react-router-dom';

const SignaturePublic = () => {
  const { actaId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [acta, setActa] = useState(null);
  const [selectedAttendee, setSelectedAttendee] = useState('');
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [showActaPreview, setShowActaPreview] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showTutorial, setShowTutorial] = useState(false);
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [previousSignatureCount, setPreviousSignatureCount] = useState(0);
  const [sendingComment, setSendingComment] = useState(false);
  const [signMethod, setSignMethod] = useState(0);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const signatureRef = useRef();
  const fileInputRef = useRef();

  // Cargar datos del acta y configurar listener en tiempo real
  useEffect(() => {
    
    if (!actaId) {
      setError('ID de acta no válido');
      setLoading(false);
      return;
    }

    const actaRef = doc(db, 'actas', actaId);
    
    // Configurar listener en tiempo real para actualizaciones automáticas
    const unsubscribe = onSnapshot(
      actaRef,
      (docSnap) => {
         if (!docSnap.exists()) {
           setError('Acta no encontrada');
           setLoading(false);
           return;
         }

         const actaData = { id: docSnap.id, ...docSnap.data() };
         
         // Convertir fecha si es necesario
         if (actaData.fecha && typeof actaData.fecha.toDate === 'function') {
           actaData.fecha = actaData.fecha.toDate();
         }
         
         // Convertir fechas de compromisos si es necesario
         if (actaData.compromisos && Array.isArray(actaData.compromisos)) {
           actaData.compromisos = actaData.compromisos.map(compromiso => {
             if (compromiso.fechaCumplimiento && typeof compromiso.fechaCumplimiento.toDate === 'function') {
               return {
                 ...compromiso,
                 fechaCumplimiento: compromiso.fechaCumplimiento.toDate()
               };
             }
             return compromiso;
           });
         }
         
         // Detectar nuevas firmas para mostrar notificación
         const currentSignatureCount = actaData.asistentes?.filter(a => a.firma).length || 0;
         
         // Solo mostrar notificación si no es la carga inicial y hay nuevas firmas
         if (!loading && currentSignatureCount > previousSignatureCount) {
           const newSignatures = currentSignatureCount - previousSignatureCount;
           setSnackbar({
             open: true,
             message: `${newSignatures === 1 ? 'Nueva firma agregada' : `${newSignatures} nuevas firmas agregadas`} por otro(s) usuario(s)`,
             severity: 'info'
           });
         }
         
         setPreviousSignatureCount(currentSignatureCount);
         
         setActa(actaData);
         setLoading(false);
       },
      (error) => {
        console.error('SignaturePublic: Error en el listener:', error);
        setError(`Error al cargar el acta: ${error.message}`);
        setLoading(false);
      }
    );

    // Cleanup function para desuscribirse del listener
    return () => {
      unsubscribe();
    };
  }, [actaId]);

  const handleAttendeeSelect = (attendeeIndex) => {
    setSelectedAttendee(attendeeIndex);
    setShowSignaturePad(true);
    setSignMethod(0);
    setUploadedImage(null);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        setSnackbar({
          open: true,
          message: 'La imagen no debe superar los 2MB',
          severity: 'warning'
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target.result);
        setOriginalImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveUploadedImage = () => {
    setUploadedImage(null);
    setOriginalImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRotate = async (direction) => {
    if (!uploadedImage) return;

    const img = new Image();
    img.src = uploadedImage;
    await new Promise(resolve => img.onload = resolve);

    const canvas = document.createElement('canvas');
    if (Math.abs(direction) === 90) {
      canvas.width = img.height;
      canvas.height = img.width;
    } else {
      canvas.width = img.width;
      canvas.height = img.height;
    }

    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((direction * Math.PI) / 180);
    ctx.drawImage(img, -img.width / 2, -img.height / 2);

    setUploadedImage(canvas.toDataURL());
  };

  const handleAutoCrop = async () => {
    if (!uploadedImage) return;

    const img = new Image();
    img.src = uploadedImage;
    await new Promise(resolve => img.onload = resolve);

    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);

    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const l = pixels.data.length;
    const bound = { top: null, left: null, right: null, bottom: null };
    let x, y;

    for (let i = 0; i < l; i += 4) {
      const r = pixels.data[i];
      const g = pixels.data[i+1];
      const b = pixels.data[i+2];
      const a = pixels.data[i+3];

      // Considerar blanco si RGB > 230 y opaco
      const isWhite = r > 230 && g > 230 && b > 230;
      const isTransparent = a === 0;

      if (!isWhite && !isTransparent) {
        x = (i / 4) % canvas.width;
        y = ~~((i / 4) / canvas.width);

        if (bound.top === null) bound.top = y;
        if (bound.left === null) bound.left = x;
        if (bound.right === null) bound.right = x;
        if (bound.bottom === null) bound.bottom = y;

        if (y > bound.bottom) bound.bottom = y;
        if (x < bound.left) bound.left = x;
        if (x > bound.right) bound.right = x;
      }
    }

    if (bound.top !== null) {
      const padding = 10;
      const width = bound.right - bound.left + 1;
      const height = bound.bottom - bound.top + 1;

      const trimmedCanvas = document.createElement('canvas');
      trimmedCanvas.width = width + (padding * 2);
      trimmedCanvas.height = height + (padding * 2);
      const trimmedCtx = trimmedCanvas.getContext('2d');

      trimmedCtx.drawImage(
        canvas, 
        bound.left, bound.top, width, height, 
        padding, padding, width, height
      );

      setUploadedImage(trimmedCanvas.toDataURL());
      setSnackbar({
        open: true,
        message: 'Imagen ajustada automáticamente',
        severity: 'success'
      });
    } else {
      setSnackbar({
        open: true,
        message: 'No se detectó contenido para recortar',
        severity: 'warning'
      });
    }
  };

  const handleResetImage = () => {
    if (originalImage) {
      setUploadedImage(originalImage);
    }
  };

  const handleSaveSignature = async () => {
    let signatureDataUrl;

    if (signMethod === 0) {
      if (!signatureRef.current || signatureRef.current.isEmpty()) {
        setSnackbar({
          open: true,
          message: 'Por favor, dibuje su firma antes de guardar',
          severity: 'warning'
        });
        return;
      }
      signatureDataUrl = signatureRef.current.toDataURL();
    } else {
      if (!uploadedImage) {
        setSnackbar({
          open: true,
          message: 'Por favor, suba una imagen de su firma',
          severity: 'warning'
        });
        return;
      }
      signatureDataUrl = uploadedImage;
    }

    try {
      // Obtener datos actuales de Firebase para evitar sobrescribir otras firmas
      const actaRef = doc(db, 'actas', acta.id);
      const currentActaSnap = await getDoc(actaRef);
      
      if (!currentActaSnap.exists()) {
        throw new Error('El acta ya no existe');
      }
      
      const currentActaData = currentActaSnap.data();
      const currentAsistentes = currentActaData.asistentes || [];
      
      // Verificar que el asistente seleccionado aún existe
      if (!currentAsistentes[parseInt(selectedAttendee)]) {
        throw new Error('El asistente seleccionado ya no existe');
      }
      
      // Verificar si ya tiene firma para evitar sobrescribir
      if (currentAsistentes[parseInt(selectedAttendee)].firma) {
        setSnackbar({
          open: true,
          message: 'Este asistente ya ha firmado',
          severity: 'warning'
        });
        setShowSignaturePad(false);
        setSelectedAttendee('');
        return;
      }
      
      // Actualizar solo el asistente específico preservando las demás firmas
      const updatedAsistentes = currentAsistentes.map((asistente, index) => {
        if (index === parseInt(selectedAttendee)) {
          // Remover autoApproved y tipoFirma si existían, para que sea una firma normal
          const updatedAsistente = { ...asistente, firma: signatureDataUrl, fechaFirma: new Date() };
          if (updatedAsistente.autoApproved) {
            delete updatedAsistente.autoApproved;
          }
          if (updatedAsistente.tipoFirma === 'auto_approved') {
            delete updatedAsistente.tipoFirma;
          }
          return updatedAsistente;
        }
        return asistente;
      });

      // Actualizar en Firebase
      await updateDoc(actaRef, {
        asistentes: updatedAsistentes
      });

      // Actualizar estado local con los datos más recientes
      setActa(prev => ({
        ...prev,
        asistentes: updatedAsistentes
      }));

      setSnackbar({
        open: true,
        message: 'Firma guardada exitosamente',
        severity: 'success'
      });

      setShowSignaturePad(false);
      setSelectedAttendee('');
    } catch (error) {
      console.error('Error al guardar la firma:', error);
      setSnackbar({
        open: true,
        message: `Error al guardar la firma: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handleClearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const handleSendComment = async () => {
    if (!commentText.trim()) {
      setSnackbar({
        open: true,
        message: 'Por favor, escriba un comentario antes de enviar',
        severity: 'warning'
      });
      return;
    }

    if (selectedAttendee === '') {
      setSnackbar({
        open: true,
        message: 'Error: No se ha seleccionado un asistente',
        severity: 'error'
      });
      return;
    }

    setSendingComment(true);

    try {
      // Obtener datos actuales de Firebase
      const actaRef = doc(db, 'actas', acta.id);
      const currentActaSnap = await getDoc(actaRef);
      
      if (!currentActaSnap.exists()) {
        throw new Error('El acta ya no existe');
      }
      
      const currentActaData = currentActaSnap.data();
      const currentAsistentes = currentActaData.asistentes || [];
      
      // Verificar que el asistente seleccionado aún existe
      if (!currentAsistentes[parseInt(selectedAttendee)]) {
        throw new Error('El asistente seleccionado ya no existe');
      }

      const asistente = currentAsistentes[parseInt(selectedAttendee)];
      
      // Verificar si ya tiene firma (no puede comentar si ya firmó)
      if (asistente.firma) {
        setSnackbar({
          open: true,
          message: 'No puede enviar comentarios porque ya ha firmado el acta',
          severity: 'warning'
        });
        setShowCommentDialog(false);
        setCommentText('');
        setSendingComment(false);
        return;
      }

      // Crear el comentario
      const nuevoComentario = {
        asistente: asistente.nombre,
        email: asistente.email,
        comentario: commentText.trim(),
        fecha: new Date(),
        estado: 'pendiente', // pendiente, aprobado, rechazado
        fechaCreacion: new Date()
      };

      // Obtener comentarios existentes o crear array vacío
      const comentariosExistentes = currentActaData.comentarios || [];
      
      // Agregar el nuevo comentario
      const comentariosActualizados = [...comentariosExistentes, nuevoComentario];

      // Actualizar en Firebase
      await updateDoc(actaRef, {
        comentarios: comentariosActualizados
      });

      setSnackbar({
        open: true,
        message: 'Comentario enviado exitosamente. Será revisado por el administrador.',
        severity: 'success'
      });

      setShowCommentDialog(false);
      setCommentText('');
      setSelectedAttendee('');
    } catch (error) {
      console.error('Error al enviar comentario:', error);
      setSnackbar({
        open: true,
        message: `Error al enviar comentario: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setSendingComment(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="h6">Cargando acta...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center" color="primary" sx={{ mb: 2 }}>
          Firma Digital - Acta #{acta?.numeroActa}
        </Typography>

        {/* Added deadline warning message */}
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body1">
            Tiene un plazo máximo de 2 días hábiles para firmar o enviar un comentario sobre este documento. Si no se registra su firma dentro de este plazo, el acta se dará por aprobada automáticamente.
          </Typography>
        </Alert>
        
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Información del Acta
        </Typography>
        
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body1"><strong>Fecha:</strong> {acta?.fecha?.toLocaleDateString('es-ES')}</Typography>
          <Typography variant="body1">
            <strong>Objetivo:</strong>{' '}
            {typeof acta?.objetivo === 'object' && acta?.objetivo !== null
              ? (acta.objetivo.textFallback || '')
              : (typeof acta?.objetivo === 'string' && acta.objetivo.trim().startsWith('{')
                ? (() => {
                    try {
                      const p = JSON.parse(acta.objetivo);
                      return p.textFallback || acta.objetivo;
                    } catch (e) {
                      return acta.objetivo;
                    }
                  })()
                : acta?.objetivo)}
          </Typography>
          <Typography variant="body1"><strong>Responsable:</strong> {acta?.responsable}</Typography>
          <Button
            variant="contained"
            onClick={() => setShowTutorial(true)}
            sx={{ 
              bgcolor: '#6164ff',
              color: 'white',
              textTransform: 'none',
              fontSize: '0.875rem',
              px: 2,
              py: 1,
              mt: 1,
              borderRadius: 2,
              '&:hover': {
                bgcolor: '#5a5dcc',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 8px rgba(97, 100, 255, 0.3)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            📹 Ver Tutorial de Firmas
          </Button>
        </Box>

        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setPdfLoading(true);
              setShowActaPreview(true);
              // Ocultar el loader después de 2 segundos para dar tiempo a que el PDF se renderice
              setTimeout(() => {
                setPdfLoading(false);
              }, 2000);
            }}
            startIcon={<VisibilityIcon />}
            sx={{ color: '#6164ff', borderColor: '#6164ff' }}
          >
            Ver Acta Completa
          </Button>
        </Box>

        {/* Progreso de firmas */}
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f0f7ff', borderRadius: 1, border: '1px solid #e3f2fd' }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
            Estado de Firmas
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {acta?.asistentes?.filter(a => a.firma).length || 0} de {acta?.asistentes?.length || 0} asistentes han firmado
          </Typography>
          <Box sx={{ 
            width: '100%', 
            height: 8, 
            bgcolor: '#e0e0e0', 
            borderRadius: 1,
            overflow: 'hidden'
          }}>
            <Box sx={{
              width: `${acta?.asistentes?.length ? (acta.asistentes.filter(a => a.firma).length / acta.asistentes.length) * 100 : 0}%`,
              height: '100%',
              bgcolor: '#4caf50',
              transition: 'width 0.3s ease'
            }} />
          </Box>
          {acta?.asistentes?.filter(a => a.firma).length === acta?.asistentes?.length && (
            <Typography variant="body2" sx={{ mt: 1, color: '#4caf50', fontWeight: 'bold' }}>
              ✓ Todos los asistentes han firmado
            </Typography>
          )}
        </Box>

        <Typography variant="h6" gutterBottom>
          Seleccione su nombre para firmar
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Seleccionar asistente</InputLabel>
          <Select
            value={selectedAttendee}
            label="Seleccionar asistente"
            onChange={(e) => setSelectedAttendee(e.target.value)}
          >
            {acta?.asistentes?.map((asistente, index) => (
              <MenuItem key={index} value={index}>
                {asistente.nombre} - {asistente.cargo}
                {asistente.firma ? ' (Ya firmado)' : asistente.autoApproved ? ' (Aprobado por tiempo límite)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedAttendee !== '' && (
          <Box sx={{ textAlign: 'center', display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={() => setShowSignaturePad(true)}
              sx={{ bgcolor: '#6164ff' }}
              disabled={acta?.asistentes?.[selectedAttendee]?.firma}
            >
              {acta?.asistentes?.[selectedAttendee]?.firma 
                ? 'Ya firmado' 
                : acta?.asistentes?.[selectedAttendee]?.autoApproved 
                  ? 'Reemplazar con mi firma' 
                  : 'Proceder a firmar'}
            </Button>
            {/* Solo mostrar botón de comentario si el usuario NO ha firmado */}
            {!acta?.asistentes?.[selectedAttendee]?.firma && (
              <Button
                variant="outlined"
                onClick={() => setShowCommentDialog(true)}
                sx={{ color: '#6164ff', borderColor: '#6164ff' }}
              >
                Diligenciar Comentario
              </Button>
            )}
          </Box>
        )}
      </Paper>

      {/* Modal de firma */}
      <Dialog
        open={showSignaturePad}
        onClose={() => setShowSignaturePad(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Firmar como: {acta?.asistentes?.[selectedAttendee]?.nombre}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs 
              value={signMethod} 
              onChange={(e, newValue) => setSignMethod(newValue)} 
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab icon={<DrawIcon />} label="Dibujar en Pantalla" />
              <Tab icon={<UploadIcon />} label="Subir Imagen" />
            </Tabs>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {signMethod === 0 
                ? 'Dibuje su firma en el área de abajo' 
                : 'Suba una imagen de su firma (PNG o JPG, máx 2MB)'}
            </Typography>
          </Box>
          
          {signMethod === 0 ? (
            <Paper sx={{ p: 2, border: '2px dashed #ccc', bgcolor: '#fafafa' }}>
              <SignatureCanvas
                ref={signatureRef}
                canvasProps={{
                  width: 500,
                  height: 200,
                  className: 'signature-canvas',
                  style: { width: '100%', height: '200px' }
                }}
              />
            </Paper>
          ) : (
            <Paper 
              sx={{ 
                p: 3, 
                border: '2px dashed #6164ff', 
                bgcolor: '#f5f6ff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '236px',
                cursor: !uploadedImage ? 'pointer' : 'default',
                transition: 'all 0.3s ease',
                '&:hover': !uploadedImage ? { bgcolor: '#eef0ff' } : {}
              }}
              onClick={() => !uploadedImage && fileInputRef.current.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/png, image/jpeg, image/jpg"
                onChange={handleImageUpload}
              />
              
              {uploadedImage ? (
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', bgcolor: '#fff', borderRadius: 1, p: 1 }}>
                    <img 
                      src={uploadedImage} 
                      alt="Firma subida" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '200px', 
                        objectFit: 'contain' 
                      }} 
                    />
                    <IconButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveUploadedImage();
                      }}
                      sx={{ 
                        position: 'absolute', 
                        top: -10, 
                        right: -10, 
                        bgcolor: 'error.main', 
                        color: 'white',
                        '&:hover': { bgcolor: 'error.dark' },
                        zIndex: 10
                      }}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      startIcon={<RotateLeftIcon />}
                      onClick={(e) => { e.stopPropagation(); handleRotate(-90); }}
                    >
                      Rotar Izq
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      startIcon={<RotateRightIcon />}
                      onClick={(e) => { e.stopPropagation(); handleRotate(90); }}
                    >
                      Rotar Der
                    </Button>
                    <Button 
                      variant="contained" 
                      size="small" 
                      startIcon={<CropIcon />}
                      onClick={(e) => { e.stopPropagation(); handleAutoCrop(); }}
                      color="secondary"
                    >
                      Auto Ajustar
                    </Button>
                    <Button 
                      variant="text" 
                      size="small" 
                      startIcon={<ResetIcon />}
                      onClick={(e) => { e.stopPropagation(); handleResetImage(); }}
                      color="error"
                    >
                      Reset
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center' }}>
                  <UploadIcon sx={{ fontSize: 48, color: '#6164ff', mb: 1 }} />
                  <Typography variant="h6" color="primary" gutterBottom>
                    Haga clic para subir imagen
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    Formatos soportados: PNG, JPG
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSignaturePad(false)}>
            Cancelar
          </Button>
          {signMethod === 0 && (
            <Button onClick={handleClearSignature} startIcon={<ClearIcon />}>
              Limpiar
            </Button>
          )}
          <Button
            onClick={handleSaveSignature}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{ bgcolor: '#6164ff' }}
            disabled={signMethod === 1 && !uploadedImage}
          >
            Guardar Firma
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de vista previa del acta */}
      <Dialog
        open={showActaPreview}
        onClose={() => setShowActaPreview(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { height: isMobile ? '100%' : '90vh' }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#6164ff',
          color: 'white'
        }}>
          Vista Previa del Acta
          <IconButton
            onClick={() => setShowActaPreview(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%', position: 'relative' }}>
          {pdfLoading && (
            <Backdrop
              sx={{
                position: 'absolute',
                zIndex: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}
              open={pdfLoading}
            >
              <CircularProgress size={60} />
              <Typography variant="h6" color="primary">
                Cargando vista previa...
              </Typography>
            </Backdrop>
          )}
          {showActaPreview && acta && (
            <PDFViewer
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
            >
              <ActaPDF data={acta} />
            </PDFViewer>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Tutorial Dialog */}
      <Dialog
        open={showTutorial}
        onClose={() => setShowTutorial(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Tutorial de Firma
          <IconButton onClick={() => setShowTutorial(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Aprende cómo firmar digitalmente las actas siguiendo este tutorial:
          </Typography>
          <Box sx={{ textAlign: 'center' }}>
            <img 
              src="https://res.cloudinary.com/dvmkjwx94/image/upload/v1751068105/w5pp29qmyjfo1s61mfrc.gif" 
              alt="Tutorial de firma" 
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </Box>
        </DialogContent>
      </Dialog>
      
      {/* Diálogo de Comentarios */}
      <Dialog
        open={showCommentDialog}
        onClose={() => !sendingComment && setShowCommentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: '#6164ff',
          color: 'white'
        }}>
          <Typography variant="h6">Diligenciar Comentario</Typography>
          <IconButton 
            onClick={() => !sendingComment && setShowCommentDialog(false)}
            sx={{ color: 'white' }}
            disabled={sendingComment}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Comentario de: <strong>{acta?.asistentes?.[selectedAttendee]?.nombre}</strong>
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Escriba su comentario"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Ingrese sus observaciones o comentarios sobre el acta..."
            sx={{ mb: 2 }}
            disabled={sendingComment}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => {
              setShowCommentDialog(false);
              setCommentText('');
            }}
            variant="outlined"
            sx={{ color: '#6164ff', borderColor: '#6164ff' }}
            disabled={sendingComment}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSendComment}
            variant="contained"
            sx={{ bgcolor: '#6164ff' }}
            disabled={sendingComment || !commentText.trim()}
          >
            {sendingComment ? 'Enviando...' : 'Enviar Comentario'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SignaturePublic;
